import { mdiCubeOutline } from '@mdi/js';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    Stack,
    SxProps,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { vars, labelFontSemiBold, bodyFont, headingFontBold } from '../../../../../Theme';
import AppIcon from '../../../Icons/AppIcon';
import CheckboxTree from '../../_parts/CheckboxTree';
import { ISaveAssemblyContentProps } from '../SaveAssemblyDialog';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../../redux/store';

const formLabelSx: SxProps = {
    ...headingFontBold,
    fontSize: '16px',
    color: vars.black,
    fontWeight: 700,
    marginBottom: '8px',

    '&.Mui-focused': {
        color: vars.black,
    },
};

const textFieldSx: SxProps = {
    input: { ...bodyFont, p: 0 },

    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
            border: `2px solid ${vars.black}`,
        },
    },
    '& .MuiFormHelperText-root': {
        marginLeft: 'unset',
    },

    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
        py: '6.5px',
        pl: '12px',
    },
};

const partialModelLabelSx: SxProps = {
    display: 'flex',
    minHeight: '40px',
    alignItems: 'center',
    fontSize: '16px',
    fontWeight: 'bold',
    fontFamily: headingFontBold,
    gap: '8px',
    color: vars.black,

    '&.Mui-focused': { color: vars.black },
};

export default ({ mainState, handleChange }: ISaveAssemblyContentProps) => {
    const { t } = useTranslation('translations', { keyPrefix: 'assemblyDialog' });

    const { data: documents } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocuments.info,
    );
    const { data: partialModels } = useSelector(
        (_store: IAppReduxStore) => _store.projectSaveAssembly.linkedPartialModels,
    );
    const docTypesConfig = useSelector((store: IAppReduxStore) => store.projectDocTypes.data);

    useEffect(() => {
        if (partialModels) {
            handleChange({
                field: 'selectedDocIdDocIndices',
                value: partialModels
                    .filter(
                        partialModel =>
                            partialModel.isLinked ||
                            documents
                                .map(document => document.IdDocIndex)
                                .includes(partialModel.idDocIndex),
                    )
                    .map(partialModel => partialModel.idDocIndex),
            });
        }
    }, [partialModels, documents]);

    const items = useMemo(() => {
        if (partialModels?.length > 0) {
            return partialModels.map(partialModel => {
                const partialModelDocTypeConfig = docTypesConfig.find(
                    docTypeConfig => docTypeConfig.name === partialModel.docType,
                );

                return {
                    id: partialModel.idDocIndex,
                    label: partialModel.subject,
                    parent: '',
                    path: partialModel.subject,
                    icon: {
                        childIcon: {
                            path: partialModelDocTypeConfig?.icon,
                            color: partialModelDocTypeConfig?.color,
                        },
                    },
                };
            });
        }
    }, [partialModels]);

    const handleNodeCheck = (id: string, isChecked: boolean) => {
        handleChange({
            field: 'selectedDocIdDocIndices',
            value: isChecked
                ? [...mainState.selectedDocIdDocIndices, id]
                : mainState.selectedDocIdDocIndices.filter(item => item !== id),
        });
    };

    const handleSelectDeselectAll = (_, checked: boolean) => {
        handleChange({
            field: 'selectedDocIdDocIndices',
            value: checked ? items.map(item => item.id) : [],
        });
    };

    return (
        <Stack spacing={'16px'}>
            <FormControl>
                <FormLabel sx={formLabelSx}>{t('index')}</FormLabel>
                <TextField
                    size='small'
                    sx={textFieldSx}
                    disabled
                    value={mainState?.index || t('indexNotFound')}
                />
            </FormControl>

            <FormControl>
                <FormLabel sx={formLabelSx}>{t('subject')}</FormLabel>
                <TextField
                    size='small'
                    sx={textFieldSx}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange({
                            field: 'subjectText',
                            value: e.target.value,
                        })
                    }
                    placeholder={t('enterText')}
                    value={mainState.subjectText}
                />
            </FormControl>

            <FormControl>
                <FormLabel sx={partialModelLabelSx}>
                    <AppIcon path={mdiCubeOutline} color={vars.font} size={1} />
                    {t('partialModels')}
                </FormLabel>
                <Stack sx={{ p: 0, pt: '8px' }}>
                    <Stack
                        direction='row'
                        sx={{
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <FormLabel required sx={{ ...formLabelSx }}>
                            {t('choosePartialModels')}
                        </FormLabel>
                        <FormControlLabel
                            disabled={!partialModels || partialModels.length === 0}
                            label={
                                partialModels?.length !== mainState?.selectedDocIdDocIndices?.length
                                    ? t('selectAll')
                                    : t('clear')
                            }
                            labelPlacement='start'
                            sx={{
                                marginBottom: '8px',
                                '& .MuiFormControlLabel-label': {
                                    fontFamily: labelFontSemiBold,
                                    fontSize: '14px',
                                },
                            }}
                            control={
                                <Checkbox
                                    disabled={!partialModels || partialModels.length === 0}
                                    sx={{ padding: 0, paddingX: '9px' }}
                                    onChange={handleSelectDeselectAll}
                                    checked={
                                        partialModels?.length ===
                                        mainState?.selectedDocIdDocIndices?.length
                                    }
                                />
                            }
                        />
                    </Stack>

                    <CheckboxTree
                        items={items ?? []}
                        checkedNodes={mainState?.selectedDocIdDocIndices}
                        onCheckNode={handleNodeCheck}
                        simpleTreeViewSx={{ overflow: 'auto', maxHeight: '360px' }}
                    />
                </Stack>
            </FormControl>
        </Stack>
    );
};
